import type { ComputedRef, Ref } from "vue";
import { useCheckout } from "@donoor/checkout";
import { capturerCommandePaypal } from "~/utils/capturePaypal";
import { suiteIntegration } from "~/utils/embed";
import type { CreateOrderPayload } from "~/types/event";

export interface PendingEventStripeOrder {
  order_id: string;
  payment_intent_id: string;
  client_secret: string;
  stripe_account_id?: string;
}

export interface UseEventCheckoutProvidersDeps {
  slug: Ref<string> | ComputedRef<string>;
  isPreview: ComputedRef<boolean> | Ref<boolean>;
  /** Événement courant — utilisé pour l'organization_id de la capture PayPal. */
  event: ComputedRef<{ organization_id?: number | string } | null>;
  stripeAccountId: ComputedRef<string | null>;
  paymentProviders: ComputedRef<{
    paypal?: { merchant_id?: string | null } | null;
  }>;
  isPaypalOnlyMode: ComputedRef<boolean>;
  usesPaypalWallets: ComputedRef<boolean>;
  selectedProvider: Ref<"stripe" | "paypal">;
  includeFees: Ref<boolean>;
  calculations: {
    amountWithFees: ComputedRef<number>;
    finalAmount: ComputedRef<number>;
  };
  buyerInfo: Ref<{
    firstName: string;
    lastName: string;
    email: string;
    confirmEmail: string;
    phone: string;
  }>;
  buildOrderPayload: () => CreateOrderPayload;
}

/**
 * useEventCheckoutProviders — instances de paiement (Stripe + PayPal) de la
 * page /b/[slug]. Extrait 1:1 de `pages/b/[slug]/index.vue` (Zone A split
 * >800 lignes).
 *
 * Provider primaire (Karim 2026-06-21) : parité /a /c /donate /pay.
 */
export function useEventCheckoutProviders(deps: UseEventCheckoutProvidersDeps) {
  const {
    slug,
    isPreview,
    event,
    stripeAccountId,
    paymentProviders,
    isPaypalOnlyMode,
    usesPaypalWallets,
    selectedProvider,
    includeFees,
    calculations,
    buyerInfo,
    buildOrderPayload,
  } = deps;
  const config = useRuntimeConfig();
  // Langue de la page : champs carte et messages Stripe/PayPal traduits.
  const { localeIntl } = useFormatLangue();
  // Widget chez l'association (`?embed=1&host=…`) : la page de succès le reste.
  const route = useRoute();

  let pendingOrderResult: PendingEventStripeOrder | null = null;

  // Order pending PayPal (rempli quand createOrder backend a renvoyé un paypal_order_id).
  let pendingPaypalOrder: {
    order_id: string;
    paypal_order_id: string;
    paypal_merchant_id?: string;
  } | null = null;

  /** Utilisé par useEventOrderSubmit pour mémoriser l'order créé au submit. */
  function setPendingStripeOrder(r: PendingEventStripeOrder): void {
    pendingOrderResult = r;
  }

  /**
   * La commande en attente change de mains : payée (succès), ou confiée à
   * la page de paiement PayPal. On l'OUBLIE ici pour qu'un départ de page
   * ne l'annule pas derrière — annuler une commande payée dont le webhook
   * n'est pas encore arrivé libérerait des places déjà vendues.
   */
  function clearPendingOrders(): void {
    pendingOrderResult = null;
    pendingPaypalOrder = null;
  }

  /**
   * LIBÉRATION IMMÉDIATE de la place (Karim 2026-09-06) : dès que l'acheteur
   * abandonne — paiement refusé puis fermé, retour à l'événement, onglet
   * fermé — la commande `pending` est annulée côté serveur et ses places
   * rendues. Avant, la place restait bloquée jusqu'au cron d'expiration
   * (30 min + 10 min de cron) sans que le site n'appelle jamais l'annulation
   * qui existait pourtant. Le cron (15 min) ne reste qu'un filet pour les
   * abandons muets (coupure réseau, wallet fermé puis onglet oublié).
   *
   * Idempotent et sans risque : le serveur n'annule QUE `pending`.
   */
  /**
   * ⚠️ Vrai pendant qu'une feuille de paiement est ouverte (Apple Pay, Google
   * Pay, PayPal). Sur iPhone, **Apple Pay s'affiche par-dessus la page web et
   * déclenche `pagehide`** — indistinguable d'un onglet qu'on ferme. Le
   * 2026-09-11, la première vraie vente de bookin'events a perdu 9 commandes
   * ainsi : 672,87 € encaissés chez Stripe, billets annulés 13 secondes après
   * l'achat, pendant que l'acheteuse validait sur son téléphone.
   *
   * Tant que ce drapeau est levé, l'abandon AUTOMATIQUE (fermeture d'onglet,
   * navigation) ne libère plus la place. L'abandon EXPLICITE — l'acheteuse
   * ferme l'erreur de paiement — passe `force: true` et reste immédiat.
   * Pour les vrais abandons pendant la feuille, le balayage des 15 minutes
   * reprend son rôle de filet.
   */
  let paiementEnVol = false;
  function marquerPaiementEnVol(): void {
    paiementEnVol = true;
  }

  async function abandonPendingOrder(
    opts: { keepalive?: boolean; force?: boolean } = {},
  ): Promise<void> {
    const orderId = pendingOrderResult?.order_id ?? pendingPaypalOrder?.order_id;
    if (!orderId) return;
    if (paiementEnVol && !opts.force) return;
    clearPendingOrders();
    const { eventsApi } = await import("~/services/api/events");
    await eventsApi.cancelOrder(orderId, buyerInfo.value.email || undefined, opts);
  }

  const checkout = useCheckout({
    provider: "stripe",
    locale: localeIntl.value,
    publicKey: config.public.stripePublishableKey as string,
    // Getter (PAS `.value` figé) : résolu à l'init du provider, sinon Stripe.js
    // s'initialise sur la plateforme → « No such payment_intent ».
    connectedAccountId: () => stripeAccountId.value || undefined,
    currency: "eur",
    // Montant présenté dans la feuille Apple Pay / Google Pay = montant
    // DÉBITÉ, frais couverts ou non. `finalAmount` (billets seuls) oubliait le
    // soutien Donoor et le don à l'association quand l'acheteur ne couvrait
    // pas les frais : la feuille affichait moins que ce qui était débité.
    amount: computed(() => calculations.amountWithFees.value),
    donor: computed(() => ({
      email: buyerInfo.value.email,
      name: `${buyerInfo.value.firstName} ${buyerInfo.value.lastName}`.trim(),
      phone: buyerInfo.value.phone,
    })),
    recurring: ref(false),
    feesCovered: includeFees,
    createPaymentIntent: async () => {
      if (pendingOrderResult?.client_secret) {
        marquerPaiementEnVol();
        return { clientSecret: pendingOrderResult.client_secret, transactionId: pendingOrderResult.payment_intent_id };
      }
      const payload = buildOrderPayload();
      const { eventsApi } = await import("~/services/api/events");
      const orderResult = await eventsApi.createOrder(slug.value, payload, isPreview.value);
      if (orderResult.free_with_promo) throw new Error("Order is free with promo, should not reach Stripe");
      pendingOrderResult = {
        order_id: orderResult.order_id,
        payment_intent_id: orderResult.payment_intent_id ?? "",
        client_secret: orderResult.client_secret ?? "",
        stripe_account_id: orderResult.stripe_account_id,
      };
      if (!pendingOrderResult.client_secret) throw new Error("Order créé sans client_secret");
      marquerPaiementEnVol();
      return { clientSecret: pendingOrderResult.client_secret, transactionId: pendingOrderResult.payment_intent_id };
    },
    successUrl: () => {
      const orderQuery = pendingOrderResult?.order_id ? `&order_id=${pendingOrderResult.order_id}` : "";
      return `/b/${slug.value}/success?email=${encodeURIComponent(buyerInfo.value.email || "")}${orderQuery}${suiteIntegration(route.query)}`;
    },
  });

  // Instance PayPal (Karim 2026-06-21) — utilisée pour CardFields PayPal
  // (preferred=paypal) ET pour les wallets/Smart Button. `createPaymentIntent`
  // crée l'order ticketing avec payment_provider="paypal" et retourne le
  // paypal_order_id consommé par le SDK PayPal.
  const paypalCheckout = useCheckout({
    provider: "paypal",
    locale: localeIntl.value,
    publicKey: (config.public.paypalClientId as string) || "",
    // Getter (PAS `.value` figé) : résolu à l'init du provider.
    connectedAccountId: () =>
      paymentProviders.value.paypal?.merchant_id || undefined,
    currency: "eur",
    // Même montant que la feuille Stripe : celui qui sera débité.
    amount: computed(() => calculations.amountWithFees.value),
    donor: computed(() => ({
      email: buyerInfo.value.email,
      name: `${buyerInfo.value.firstName} ${buyerInfo.value.lastName}`.trim(),
      phone: buyerInfo.value.phone,
    })),
    recurring: ref(false),
    feesCovered: includeFees,
    partnerAttributionId: (config.public.paypalPartnerAttributionId as string) || "DONOOR_SP_PPCP",
    // buyer-country réservé au sandbox (refusé en prod → 400 SDK).
    sandbox: (config.public.mode as string) !== "prod",
    pageType: "checkout",
    createPaymentIntent: async () => {
      if (pendingPaypalOrder?.paypal_order_id) {
        marquerPaiementEnVol();
        return {
          clientSecret: pendingPaypalOrder.paypal_order_id,
          transactionId: pendingPaypalOrder.paypal_order_id,
        };
      }
      selectedProvider.value = "paypal";
      const payload = buildOrderPayload();
      const { eventsApi } = await import("~/services/api/events");
      const orderResult = await eventsApi.createOrder(slug.value, payload, isPreview.value);
      if (orderResult.free_with_promo) throw new Error("Order is free with promo, should not reach PayPal");
      if (!orderResult.paypal_order_id) throw new Error("Order créé sans paypal_order_id");
      pendingPaypalOrder = {
        order_id: orderResult.order_id,
        paypal_order_id: orderResult.paypal_order_id,
        paypal_merchant_id: orderResult.paypal_merchant_id,
      };
      // Comme Stripe et les essais PayPal suivants : la fenêtre PayPal est
      // ouverte, la redirection vers « merci » après encaissement ne doit pas
      // annuler la commande (2026-10-06).
      marquerPaiementEnVol();
      return {
        clientSecret: pendingPaypalOrder.paypal_order_id,
        transactionId: pendingPaypalOrder.paypal_order_id,
      };
    },
    successUrl: () => {
      const orderQuery = pendingPaypalOrder?.order_id
        ? `&order_id=${pendingPaypalOrder.order_id}`
        : "";
      return `/b/${slug.value}/success?email=${encodeURIComponent(buyerInfo.value.email || "")}${orderQuery}${suiteIntegration(route.query)}`;
    },
    // Lève une erreur si PayPal refuse (carte refusée…) : jamais de « merci »
    // sans paiement réel (cf. utils/capturePaypal.ts).
    onCapture: (orderId) => {
      const orgId =
        typeof event.value?.organization_id === "number"
          ? event.value.organization_id
          : Number(event.value?.organization_id);
      return capturerCommandePaypal(config.public.apiBaseUrl as string, orderId, orgId);
    },
  });

  const { mountPrimaryWallets } = useWalletsMount({
    stripeCheckout: checkout,
    paypalCheckout,
    isPaypalOnlyMode,
    usesPaypalWallets,
  });

  return {
    checkout,
    paypalCheckout,
    mountPrimaryWallets,
    setPendingStripeOrder,
    clearPendingOrders,
    abandonPendingOrder,
  };
}
